import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CurrencyProvider } from './context/CurrencyContext';
import '@/App.css';
import '@/index.css';

import { Toaster } from './components/ui/sonner';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PushNotificationPrompt from './components/PushNotificationPrompt';
import OperatorRoute from './components/OperatorRoute';

const GuestJoin = React.lazy(() => import('./pages/GuestJoin'));
const VenueEntry = React.lazy(() => import('./pages/VenueEntry'));
const GuestUpgrade = React.lazy(() => import('./pages/GuestUpgrade'));
const ShoutoutFlow = React.lazy(() => import('./pages/ShoutoutFlow'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const SignIn = React.lazy(() => import('./pages/SignIn'));
const SignUpSelector = React.lazy(() => import('./pages/SignUpSelector'));
const SignUpUser = React.lazy(() => import('./pages/SignUpUser'));
const SignUpDJ = React.lazy(() => import('./pages/SignUpDJ'));
const SignUpVenue = React.lazy(() => import('./pages/SignUpVenue'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
const UserDashboard = React.lazy(() => import('./pages/UserDashboard'));
const SessionDiscovery = React.lazy(() => import('./pages/SessionDiscovery'));
const RequestFlow = React.lazy(() => import('./pages/RequestFlow'));
const MyRequests = React.lazy(() => import('./pages/MyRequests'));
const UserProfile = React.lazy(() => import('./pages/UserProfile'));
const RequestHistory = React.lazy(() => import('./pages/RequestHistory'));
const DJDashboard = React.lazy(() => import('./pages/DJDashboard'));
const DJSessionCreate = React.lazy(() => import('./pages/DJSessionCreate'));
const DJLiveQueue = React.lazy(() => import('./pages/DJLiveQueue'));
const SessionAnalytics = React.lazy(() => import('./pages/SessionAnalytics'));
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = React.lazy(() => import('./pages/PaymentCancel'));
const DJOnboarding = React.lazy(() => import('./pages/DJOnboarding'));
const VenueOnboarding = React.lazy(() => import('./pages/VenueOnboarding'));
const DiscoverPage = React.lazy(() => import('./pages/DiscoverPage'));
const PublicProfile = React.lazy(() => import('./pages/PublicProfile'));
const ProfileSettings = React.lazy(() => import('./pages/ProfileSettings'));
const SmartRedirect = React.lazy(() => import('./components/SmartRedirect'));
const AdminSignUp = React.lazy(() => import('./pages/AdminSignUp'));
const AdminSignIn = React.lazy(() => import('./pages/AdminSignIn'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminOverview = React.lazy(() => import('./pages/admin/AdminOverview'));
const AdminUsers = React.lazy(() => import('./pages/admin/AdminUsers'));
const AdminFinances = React.lazy(() => import('./pages/admin/AdminFinances'));
const AdminReports = React.lazy(() => import('./pages/admin/AdminReports'));
const AdminMonitoring = React.lazy(() => import('./pages/admin/AdminMonitoring'));
const AdminSettings = React.lazy(() => import('./pages/admin/AdminSettings'));
const Leaderboards = React.lazy(() => import('./pages/Leaderboards'));
const ActivityFeed = React.lazy(() => import('./pages/ActivityFeed'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const FeaturesPage = React.lazy(() => import('./pages/FeaturesPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));



const LoadingScreen = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      <p className="mt-4 text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// Protected Route wrapper component
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    const sessionId = location.pathname === '/dashboard' ? new URLSearchParams(location.search).get('session') : location.pathname.match(/^\/session\/([^/]+)\//)?.[1];
    if (sessionId) return <Navigate to={`/join/${encodeURIComponent(sessionId)}`} replace />;
  }
  if (user?.is_guest && ['/settings','/profile','/history','/feed','/leaderboards'].includes(location.pathname)) return <Navigate to="/guest/upgrade" replace />;
  return user ? children : <Navigate to={`/signin?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />;
};

function AppRoutes() {
  const { user, loading, authError, refreshUser } = useAuth();
  const location = useLocation();
  React.useLayoutEffect(() => {
    document.documentElement.classList.toggle('app-theme', location.pathname !== '/');
    return () => document.documentElement.classList.remove('app-theme');
  }, [location.pathname]);

  React.useEffect(() => {
    // Register service worker
    if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.log('Service Worker registration failed:', error);
        });
    }
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  if (authError) return <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6" role="alert"><p>{authError}</p><button className="underline" onClick={refreshUser}>Retry connection</button></div>;

  return (
    <div className={location.pathname === "/" ? "App" : "App app-theme"}>
      <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={user ? <SmartRedirect /> : <LandingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/signin" element={user && !user.is_guest ? <SmartRedirect /> : <SignIn />} />
        <Route path="/signup" element={user ? <SmartRedirect /> : <SignUpSelector />} />
        <Route path="/signup/user" element={user ? <SmartRedirect /> : <SignUpUser />} />
        <Route path="/signup/dj" element={user ? <SmartRedirect /> : <SignUpDJ />} />
        <Route path="/signup/venue" element={user ? <SmartRedirect /> : <SignUpVenue />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/callback" element={<ProtectedRoute><SmartRedirect /></ProtectedRoute>} />

        {/* Public Routes - Discover should be public */}
        <Route path="/join/:sessionId" element={<GuestJoin />} />
        <Route path="/venue/:venueId" element={<VenueEntry />} />
        <Route path="/guest/upgrade" element={<ProtectedRoute><GuestUpgrade /></ProtectedRoute>} />
        <Route path="/session/:sessionId/shoutout" element={<ProtectedRoute><ShoutoutFlow /></ProtectedRoute>} />
        <Route path="/discover" element={<DiscoverPage />} />
        <Route path="/profile/:id" element={<ProtectedRoute><PublicProfile /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
        <Route path="/sessions" element={<ProtectedRoute><SessionDiscovery user={user} /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
        <Route path="/session/:sessionId/request" element={<ProtectedRoute><RequestFlow user={user} /></ProtectedRoute>} />
        <Route path="/session/:sessionId/my-requests" element={<ProtectedRoute><MyRequests user={user} /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><UserProfile user={user} /></ProtectedRoute>} />
        <Route path="/history" element={<ProtectedRoute><RequestHistory user={user} /></ProtectedRoute>} />
        <Route path="/leaderboards" element={<ProtectedRoute><Leaderboards /></ProtectedRoute>} />
        <Route path="/feed" element={<ProtectedRoute><ActivityFeed /></ProtectedRoute>} />
        
        {/* Payment Routes */}
        <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
        <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
        
        {/* Onboarding Routes */}
        <Route path="/onboarding/dj" element={<OperatorRoute onboardingRole="dj"><DJOnboarding /></OperatorRoute>} />
        <Route path="/onboarding/venue" element={<OperatorRoute onboardingRole="venue"><VenueOnboarding /></OperatorRoute>} />
        
        {/* DJ Routes (with onboarding check) */}
        <Route path="/dj" element={<OperatorRoute><DJDashboard /></OperatorRoute>} />
        <Route path="/dj/create" element={<OperatorRoute><DJSessionCreate user={user} /></OperatorRoute>} />
        <Route path="/dj/session/:sessionId" element={<OperatorRoute><DJLiveQueue user={user} /></OperatorRoute>} />
        <Route path="/dj/session/:sessionId/analytics" element={<OperatorRoute><SessionAnalytics /></OperatorRoute>} />
        
        {/* Admin Routes */}
        <Route path="/admin/signup" element={<AdminSignUp />} />
        <Route path="/admin/signin" element={<AdminSignIn />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />}>
          <Route index element={<AdminOverview />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="finances" element={<AdminFinances />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="monitoring" element={<AdminMonitoring />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>
        <Route path="*" element={<div className="min-h-screen flex flex-col items-center justify-center gap-4"><h1 className="text-2xl">Page not found</h1><a href="/discover">Find a live session</a></div>} />
      </Routes>
      </Suspense>
      <Toaster position="top-center" richColors />
      <PWAInstallPrompt />
      {user && !user.is_guest && <PushNotificationPrompt user={user} />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CurrencyProvider>
          <AppRoutes />
        </CurrencyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
